'use client';

interface VideoEmbedProps {
  src: string;
  aspectRatio?: '16:9' | '4:3' | '1:1';
  className?: string;
}

export default function VideoEmbed({
  src,
  aspectRatio = '16:9',
  className = '',
}: VideoEmbedProps) {
  // Helper function to extract video ID and create proper embed URL
  const getEmbedUrl = (url: string): string => {
    // Check if url is valid
    if (!url || typeof url !== 'string') {
      console.warn('VideoEmbed: Invalid URL provided:', url);
      return '';
    }

    // YouTube URL patterns
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const youtubeRegex =
        /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
      const match = url.match(youtubeRegex);
      if (match) {
        return `https://www.youtube.com/embed/${match[1]}?rel=0`;
      }
    }

    // Vimeo URL patterns
    if (url.includes('vimeo.com')) {
      const vimeoRegex = /vimeo\.com\/(?:.*\/)?(\d+)/;
      const match = url.match(vimeoRegex);
      if (match) {
        return `https://player.vimeo.com/video/${match[1]}?title=0&byline=0&portrait=0`;
      }
    }

    // If it's already an embed URL or other iframe source, return as-is
    return url;
  };

  const aspectRatioClasses = {
    '16:9': 'aspect-video',
    '4:3': 'aspect-[4/3]',
    '1:1': 'aspect-square',
  };

  const embedUrl = getEmbedUrl(src);

  return (
    <div
      className={`relative w-full ${aspectRatioClasses[aspectRatio]} ${className}`}
    >
      <iframe
        src={embedUrl}
        className="absolute inset-0 h-full w-full rounded-lg"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}
